/**
 * Copyright (c) 2026, CYBRA Corp.
 */
import {
    Component,
    ViewChild,
    Input,
    OnInit,
    Output,
    EventEmitter,
    ElementRef
} from '@angular/core';

import {
    DataMappingService,
    DataMappingHeaderDto,
    ObjectPropertyMappingVM
} from 'app/services/data-mapping.service';

import { ObjectService } from 'app/services/object.service';
import { ObjectCategory } from 'app/models/object-category';
import { ObjectDefinition } from 'app/models/object-definition';
import { Observable, of } from 'rxjs';
import {
    ERROR_DIALOG_TITLE,
    SimpleSuccessErrorDialogComponent
} from '../simple-success-error-dialog-box/simple-success-error-dialog-box.component';

declare const $: any;

@Component({
    selector: 'app-data-mapping-dtl-dialog',
    templateUrl: './data-mapping-DTL.dialogs.component.html'
})
export class DataMappingDtlDialogComponent implements OnInit {

    /* ================= Dialog refs ================= */

    @ViewChild('successErrorDialog', { static: false })
    successErrorDialog!: SimpleSuccessErrorDialogComponent;

    @ViewChild('csvFileInput', { static: false })
    csvFileInput!: ElementRef<HTMLInputElement>;

    /* ================= Inputs / Outputs ================= */

    @Input() headers: DataMappingHeaderDto[] = [];

    @Output()
    saved = new EventEmitter<void>();

    /* ================= UI State ================= */

    isUiDisabled = false;
    isEditMode = false;

    /* ================= Header fields ================= */

    name: string | null = null;
    selectedHeader?: DataMappingHeaderDto;

    selectedCategory: ObjectCategory | null = null;
selectedObject: ObjectDefinition | null = null;

    categories: ObjectCategory[] = [];
    objects: ObjectDefinition[] = [];

    /* ================= Property mapping ================= */

    objectParams: ObjectPropertyMappingVM[] = [];
    csvHeaders: string[] = [];
    csvHeaderResults$!: Observable<string[]>;

    constructor(
        private dataMappingService: DataMappingService,
        private objectService: ObjectService
    ) { }

    /* ================= Init ================= */

    ngOnInit(): void {
        this.categories = Object.keys(ObjectCategory)
            .filter(k => isNaN(Number(k)))
            .map(k => ObjectCategory[k as keyof typeof ObjectCategory]);
    }

    /* =====================================================
     * BACKEND → UI MAPPING HELPERS
     * ===================================================== */

    /** Convert backend ObjectProperty → ObjectPropertyMappingVM */
    private toMappingVM(p: any): ObjectPropertyMappingVM {
        return {
            ...p,
            propertyId: p.id,
            isStatic: false,
            lookUp: false,
            valueOrColumn: null
        };
    }

    /**
     * Create Name / Description mapping rows.
     * Works EVEN if there are zero backend properties.
     */
    private createInstanceField(
        template: ObjectPropertyMappingVM | null,
        id: number,
        key: string,
        label: string
    ): ObjectPropertyMappingVM {
        return {
            ...(template ?? {} as ObjectPropertyMappingVM),
            id,
            propertyId: id,
            property: key,
            name: label,
            isStatic: false,
            lookUp: false,
            valueOrColumn: null
        };
    }

    /* ================= Open dialog ================= */

    showAdd(): void {
        this.resetForm();
        this.isEditMode = false;
        
        $('#editDataMappingDtlDialog').modal('show');
    }

    async showEdit(header: DataMappingHeaderDto): Promise<void> {
        this.resetForm();
        this.isEditMode = true;
        this.selectedHeader = header;
        this.name = header.mappingName;

        this.selectedCategory =
            ObjectCategory[header.objectCategory as keyof typeof ObjectCategory];

        this.isUiDisabled = true;

        try {
            this.objects =
                await this.objectService.getAllObjectsForCategory(this.selectedCategory);

            this.selectedObject =
                this.objects.find(o => o.id === header.objectId)!;

            await this.loadPropertiesForEdit(header);

        } catch (err) {
            this.showError(err);
        } finally {
            this.isUiDisabled = false;
            $('#editDataMappingDtlDialog').modal('show');
        }
    }

    /* ================= Cascades ================= */

    async onCategoryChange(category: ObjectCategory | null): Promise<void> {
        this.selectedCategory = category || undefined;
        this.selectedObject = null;
        this.objects = [];
        this.objectParams = [];

        if (!category) return;

        this.isUiDisabled = true;
        try {
            this.objects = await this.objectService.getAllObjectsForCategory(category);
        } catch (err) {
            this.showError(err);
        } finally {
            this.isUiDisabled = false;
        }
    }

    async onObjectChange(object: ObjectDefinition | null): Promise<void> {
        if (!object) {
            this.objectParams = [];
            return;
        }

        this.selectedObject = object;
        this.isUiDisabled = true;

        try {
            const props =
                await this.objectService.getPropertiesForObjectType(object.id);

            const mappedProps = props.map(p => this.toMappingVM(p));
            const template = mappedProps.length ? mappedProps[0] : null;

            this.objectParams = [
                this.createInstanceField(template, -1, '__INSTANCE_NAME__', 'Name'),
                this.createInstanceField(template, -2, '__INSTANCE_DESC__', 'Description'),
                ...mappedProps
            ];

        } catch (err) {
            this.showError(err);
        } finally {
            this.isUiDisabled = false;
        }
    }

    /* ================= Edit loader ================= */

    private async loadPropertiesForEdit(
        header: DataMappingHeaderDto
    ): Promise<void> {

        const props =
            await this.objectService
                .getPropertiesForObjectType(this.selectedObject!.id);

        // index saved mappings by propertyId (includes -1 / -2)
        const mapped = new Map(
            (header.columns || []).map(c => [c.propertyId, c])
        );

        const mappedProps = props.map(p => {
            const vm = this.toMappingVM(p);
            const saved = mapped.get(vm.propertyId);

            return {
                ...vm,
                isStatic: Boolean(saved?.isStatic),
                lookUp: Boolean(saved?.lookUp),
                valueOrColumn: saved?.valueOrColumn ?? null
            };
        });

        const template = mappedProps.length ? mappedProps[0] : null;

        const nameField = this.createInstanceField(
            template,
            -1,
            '__INSTANCE_NAME__',
            'Name'
        );

        const descField = this.createInstanceField(
            template,
            -2,
            '__INSTANCE_DESC__',
            'Description'
        );

        // hydrate saved values for Name / Description
        const savedName = mapped.get(-1);
        if (savedName) {
            nameField.isStatic = savedName.isStatic;
            nameField.valueOrColumn = savedName.valueOrColumn;
        }

        const savedDesc = mapped.get(-2);
        if (savedDesc) {
            descField.isStatic = savedDesc.isStatic;
            descField.valueOrColumn = savedDesc.valueOrColumn;
        }

        this.objectParams = [
            nameField,
            descField,
            ...mappedProps
        ];
    }



    /* ================= CSV ================= */

    onCsvFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files?.length) return;

        const reader = new FileReader();
        reader.onload = () => {
            const firstLine = String(reader.result || '')
                .split(/\r?\n/)[0];

            this.csvHeaders = firstLine
                .split(',')
                .map(h => h.trim())
                .filter(Boolean);

            this.csvHeaderResults$ = of(this.csvHeaders);
        };

        reader.readAsText(input.files[0]);
    }

    /* ================= SAVE ================= */

    async onSaveClicked(): Promise<void> {

        if (!this.name?.trim()) {
            this.showError('Mapping Name is required');
            return;
        }
        if (!this.selectedCategory) {
            this.showError('Category is required');
            return;
        }
        if (!this.selectedObject) {
            this.showError('Object is required');
            return;
        }

        const columns = this.objectParams
            .filter(p => !!p.valueOrColumn)
            .map(p => ({
                // ✅ ALWAYS send propertyId (including -1 / -2)
                propertyId: p.propertyId,
                isStatic: p.isStatic,
                // lookup only makes sense for real object properties
                lookUp: p.propertyId > 0 ? p.lookUp : false,
                valueOrColumn: p.valueOrColumn!.trim(),
                isActive: true
            }));

        if (!columns.length) {
            this.showError('At least one property must be mapped');
            return;
        }

        const payload: DataMappingHeaderDto = {
            id: this.isEditMode ? this.selectedHeader!.id : undefined,
            mappingName: this.name.trim(),
            objectId: this.selectedObject.id,
            objectUuid: this.selectedObject.uuid,
            objectName: this.selectedObject.name,
            objectCategory: this.selectedCategory,
            active: true,
            columns
        };

        this.isUiDisabled = true;

        try {
            if (this.isEditMode) {
                await this.dataMappingService.updateHeader(payload);
            } else {
                await this.dataMappingService.createHeader(payload);
            }

            this.saved.emit();
            this.hideDialog();

        } catch (err) {
            this.showError(err);
        } finally {
            this.isUiDisabled = false;
        }
    }


    /* ================= Helpers ================= */

    hideDialog(): void {
        this.resetForm();
        $('#editDataMappingDtlDialog').modal('hide');
    }

    onCancelClicked(): void {
        this.hideDialog();
    }

    private resetForm(): void {
    this.name = null;
    this.selectedHeader = undefined;
    this.selectedCategory = undefined;
    this.selectedObject = undefined;
    this.objects = [];
    this.objectParams = [];

    // ✅ CLEAR CSV STATE
    this.csvHeaders = [];
    this.csvHeaderResults$ = of([]);  

    this.isUiDisabled = false;
    this.isEditMode = false;

    if (this.csvFileInput) {
        this.csvFileInput.nativeElement.value = '';
    }
}

    private showError(err: any): void {
        this.successErrorDialog.showErrorDialog(
            ERROR_DIALOG_TITLE,
            err?.error || err?.message || err || 'Unexpected error occurred'
        );
    }

    trackByObjectId(_: number, obj: ObjectDefinition): number {
        return obj.id;
    }
}
